import tw from "twin.macro"
import Image from "next/image"
import React, { useState } from "react"

interface ContextImageProps {
  imageUrl: string
  caption?: string
}

const CloseIcon = () => (
  <svg
    width="45"
    height="45"
    viewBox="0 0 45 45"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    tw="absolute top-6 right-6 hover:(cursor-pointer fill-current)"
  >
    <circle cx="22.5" cy="22.5" r="22.5" fill="#D9D9D9" fillOpacity="0.35" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M21.8758 21.8783C22.3444 21.4134 23.1042 21.4134 23.5728 21.8783L33.1728 31.4011C33.6414 31.866 33.6414 32.6197 33.1728 33.0845C32.7041 33.5494 31.9444 33.5494 31.4757 33.0845L22.7243 24.4034L13.9728 33.0845C13.5042 33.5494 12.7444 33.5494 12.2758 33.0845C11.8072 32.6197 11.8072 31.866 12.2758 31.4011L21.8758 21.8783Z"
      fill="white"
      fillOpacity="0.85"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M23.5729 23.3881C23.1043 23.853 22.3445 23.853 21.8759 23.3881L12.2759 13.8653C11.8073 13.4004 11.8073 12.6467 12.2759 12.1819C12.7445 11.717 13.5043 11.717 13.9729 12.1819L22.7244 20.863L31.4758 12.1819C31.9445 11.717 32.7043 11.717 33.1729 12.1819C33.6415 12.6467 33.6415 13.4004 33.1729 13.8653L23.5729 23.3881Z"
      fill="white"
      fillOpacity="0.85"
    />
  </svg>
)

const ZoomIcon = () => (
  <svg
    width="25"
    height="25"
    viewBox="0 0 25 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    tw="absolute bottom-0.5 right-0.5 scale-75 tablet-lg:(bottom-2.5 right-2.5 scale-100)"
  >
    <circle cx="12.5" cy="12.5" r="12.5" fill="#D9D9D9" fillOpacity="0.50" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.22261 6.8666C6.22261 6.48307 6.53352 6.17215 6.91706 6.17215L14.7738 6.17215C15.1573 6.17215 15.4682 6.48307 15.4682 6.8666C15.4682 7.25013 15.1573 7.56104 14.7738 7.56104L7.6115 7.56104V14.7233C7.6115 15.1069 7.30059 15.4178 6.91706 15.4178C6.53352 15.4178 6.22261 15.1069 6.22261 14.7233V6.8666Z"
      fill="white"
      fillOpacity="1"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.9056 18.1607C18.9056 18.5442 18.5946 18.8551 18.2111 18.8551L10.3544 18.8551C9.97085 18.8551 9.65993 18.5442 9.65993 18.1607C9.65993 17.7771 9.97085 17.4662 10.3544 17.4662L17.5167 17.4662L17.5167 10.3039C17.5167 9.9204 17.8276 9.60948 18.2111 9.60948C18.5947 9.60948 18.9056 9.9204 18.9056 10.3039L18.9056 18.1607Z"
      fill="white"
      fillOpacity="1"
    />
  </svg>
)

const ContextualImage = (props: ContextImageProps) => {
  const { imageUrl, caption } = props
  const [viewerVisible, setViewerVisible] = useState(false)

  const toggleViewer = async () => {
    // e.preventDefault()
    if (!!viewerVisible) {
      setViewerVisible(false)
    } else {
      setViewerVisible(true)
    }
  }

  const altText = caption
    ? `${caption.toString()} – The Richard Pousette-Dart Foundation`
    : `The Richard Pousette-Dart Foundation`

  return (
    <>
      {/* Smaller Image */}
      <div tw="w-24 tablet-lg:(w-40)">
        <div tw="relative hover:(cursor-pointer)" onClick={toggleViewer}>
          <Image
            src={imageUrl}
            alt={altText}
            width={160}
            height={160}
            sizes="(max-width: 768px) 100vw,
              (max-width: 1200px) 50vw,
              33vw"
            tw="object-scale-down"
          />
          <ZoomIcon />
        </div>
      </div>

      {/* Larger Image */}
      <div
        tw="flex flex-col items-center justify-center space-y-4 fixed w-0 h-0 -z-40 top-1/2 left-1/4 -translate-x-1/4 -translate-y-1/2 tablet:(left-1/3) desktop:(space-x-6 space-y-0 flex-row top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2) transition transform ease-in-out scale-50 opacity-0 duration-150"
        css={[viewerVisible && tw`scale-100 opacity-100 z-40 w-[90%] h-[65%] desktop:(h-[85%]) tablet:(w-2/3)`]}
        onClick={toggleViewer}
      >
        <Image
          src={imageUrl}
          alt={`${caption}`}
          width={300}
          height={300}
          sizes="(max-width: 768px) 100vw,
            33vw"
          tw="cursor-default object-scale-down max-h-full max-w-full desktop:(w-mobile-lg) desktop-lg:(w-tablet)"
        />
        <div tw="desktop:(w-80 min-h-card-lg) flex">
          <p tw="text-sm text-white font-sans text-center desktop:(text-left)">{caption}</p>
        </div>
      </div>

      {/* background overlay */}
      <div
        tw="fixed top-0 bottom-0 left-0 right-0 -z-20 bg-rpd-gray-8 w-0 h-0   items-center justify-center opacity-0 cursor-pointer transition-opacity ease-in-out delay-75 duration-300"
        css={[viewerVisible && tw`opacity-100 flex w-screen h-screen z-20`]}
        onClick={toggleViewer}
      >
        <div tw="cursor-pointer z-20" onClick={toggleViewer}>
          <CloseIcon />
        </div>
      </div>
    </>
  )
}

export default ContextualImage
